import React, { useState, useEffect } from 'react';
import { userService } from '../../services/userService';
import { useDepartments } from '../../contexts/DepartmentContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuthContext } from '../../contexts/AuthContext';
import { User } from '../../types/user';
import { Building } from 'lucide-react';
import OrganizationChart from './OrganizationChart';

const OrganizationSection: React.FC = () => {
    const { user } = useAuthContext();
    const [profile, setProfile] = useState<User | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedDepartmentId, setSelectedDepartmentId] = useState<number | null>(null);
    const [hasSetInitialDepartment, setHasSetInitialDepartment] = useState(false);
    const { departments } = useDepartments();
    const { theme } = useTheme();

    useEffect(() => {
        fetchData();
    }, []);

    useEffect(() => {
        // Auto-select user's department when profile (or auth user) and departments are loaded
        const targetProfile = profile || user;

        if (targetProfile && !hasSetInitialDepartment && departments.length > 0) {
            let targetDeptId = targetProfile.department_id;

            // Fallback: Check if user is a manager of any department
            if (!targetDeptId) {
                const managedDept = departments.find(d => d.manager_id === targetProfile.id);
                if (managedDept) {
                    targetDeptId = managedDept.id;
                }
            }

            if (targetDeptId) {
                // Verify the department exists in the list before selecting it
                const departmentExists = departments.some(d => d.id === targetDeptId);
                if (departmentExists) {
                    setSelectedDepartmentId(targetDeptId);
                    setHasSetInitialDepartment(true);
                }
            }
        }
    }, [profile, user, hasSetInitialDepartment, departments]);

    const fetchData = async () => {
        try {
            const [userProfile, allUsers] = await Promise.all([
                userService.getProfile(),
                userService.getUsers()
            ]);
            setProfile(userProfile);
            setUsers(allUsers);
        } catch (error) {
            console.error('Error fetching organization data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleDepartmentChange = (deptId: number) => {
        setSelectedDepartmentId(deptId);
    };

    const selectedDepartment = departments.find(d => d.id === selectedDepartmentId);

    // Get department members
    const departmentUsers = users.filter(u => u.department_id === selectedDepartmentId);

    // Include manager if they're not already in the department members list
    const usersToDisplay = React.useMemo(() => {
        if (!selectedDepartment?.manager_id) return departmentUsers;

        const managerAlreadyIncluded = departmentUsers.some(u => u.id === selectedDepartment.manager_id);
        if (managerAlreadyIncluded) return departmentUsers;

        // Find and add the manager
        const manager = users.find(u => u.id === selectedDepartment.manager_id);
        return manager ? [manager, ...departmentUsers] : departmentUsers;
    }, [departmentUsers, selectedDepartment, users]);

    if (loading) return <div className={theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}>Loading organization data...</div>;

    return (
        <div className="space-y-6">
            <div className={`p-6 rounded-lg shadow ${theme === 'dark' ? 'bg-gray-800' : 'bg-white'}`}>
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
                    <h3 className={`text-lg font-medium flex items-center gap-2 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                        <Building size={20} />
                        Organization Structure
                    </h3>

                    <div className="w-full md:w-64">
                        <select
                            value={selectedDepartmentId || ''}
                            onChange={(e) => handleDepartmentChange(Number(e.target.value))}
                            className={`w-full border rounded-md px-3 py-2 ${theme === 'dark' ? 'bg-gray-700 border-gray-600 text-gray-100' : 'bg-white border-gray-300 text-gray-900'}`}
                        >
                            <option value="">Select Department</option>
                            {departments.map(dept => (
                                <option key={dept.id} value={dept.id}>{dept.name}</option>
                            ))}
                        </select>
                    </div>
                </div>

                {selectedDepartmentId ? (
                    <div>
                        <div className={`mb-4 pb-4 border-b ${theme === 'dark' ? 'border-gray-700' : 'border-gray-200'}`}>
                            <h4 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                                {selectedDepartment?.name}
                            </h4>
                            {selectedDepartment?.description && (
                                <p className={`mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {selectedDepartment.description}
                                </p>
                            )}
                        </div>

                        <OrganizationChart
                            users={usersToDisplay}
                            managerId={selectedDepartment?.manager_id}
                        />
                    </div>
                ) : (
                    <div className={`text-center py-10 ${theme === 'dark' ? 'text-gray-500' : 'text-gray-400'}`}>
                        Select a department to view its organization structure
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrganizationSection;
